import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ChevronRight, Lightbulb, AlertCircle, Info } from "lucide-react";
import {
  StructuredExplanation as StructuredExplanationType,
  ExplanationStep,
  ExplanationExample,
  ConditionalBranch,
  isStep,
  isFormulaStep,
  isConditionalStep,
} from "../types/structuredExplanation";

interface StructuredExplanationProps {
  explanation: StructuredExplanationType;
}

function ExampleRenderer({ example }: { example: ExplanationExample }) {
  return (
    <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300 my-1">
      <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <code
          className={`font-mono text-sm px-2 py-1 rounded ${
            example.highlight
              ? "bg-yellow-100 dark:bg-yellow-900 font-semibold"
              : "bg-gray-100 dark:bg-gray-800"
          }`}
        >
          {example.calculation}
        </code>
        {example.note && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
            {example.note}
          </p>
        )}
      </div>
    </div>
  );
}

function ConditionalBranchRenderer({ branch }: { branch: ConditionalBranch }) {
  return (
    <div className="border-l-2 border-blue-400 pl-3 py-2 my-2">
      <div className="flex items-start gap-2 mb-2">
        <Badge className="bg-green-600 hover:bg-green-700 text-xs">If</Badge>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {branch.condition}
        </p>
      </div>
      <div className="flex items-start gap-2 ml-4">
        <Badge className="bg-blue-600 hover:bg-blue-700 text-xs">Then</Badge>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {branch.action}
        </p>
      </div>
      {branch.formula && (
        <div className="ml-4 mt-1">
          <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
            {branch.formula}
          </code>
        </div>
      )}
      {branch.conversion && (
        <div className="ml-4 mt-2 space-y-1">
          {Object.entries(branch.conversion).map(([key, value]) => (
            <div
              key={key}
              className="text-xs text-gray-600 dark:text-gray-400 flex gap-2"
            >
              <span className="font-mono">
                {key} → {value}
              </span>
            </div>
          ))}
        </div>
      )}
      {branch.examples && branch.examples.length > 0 && (
        <div className="ml-4 mt-2 space-y-1">
          {branch.examples.map((example, idx) => (
            <ExampleRenderer key={idx} example={example} />
          ))}
        </div>
      )}
    </div>
  );
}

function StepRenderer({
  step,
  number,
}: {
  step: ExplanationStep;
  number: number;
}) {
  if (isStep(step)) {
    return (
      <div className="border-l-4 border-blue-500 pl-4 py-2">
        <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-gray-200">
          Step {number}: {step.title}
        </h4>
        {step.content && (
          <p className="text-gray-700 dark:text-gray-300 mb-2">
            {step.content}
          </p>
        )}
        {step.description && (
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
            {step.description}
          </p>
        )}
        {step.formula && (
          <div className="my-2">
            {Array.isArray(step.formula) ? (
              step.formula.map((f, idx) => (
                <code
                  key={idx}
                  className="block font-mono bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded mb-1"
                >
                  {f}
                </code>
              ))
            ) : (
              <code className="block font-mono bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded">
                {step.formula}
              </code>
            )}
          </div>
        )}
        {step.important && (
          <div className="flex gap-2 items-start p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg my-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {step.important}
            </p>
          </div>
        )}
        {step.examples && step.examples.length > 0 && (
          <div className="ml-4 mt-3 space-y-1">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Examples:
            </p>
            {step.examples.map((example, idx) => (
              <ExampleRenderer key={idx} example={example} />
            ))}
          </div>
        )}
        {step.memoryTrick && (
          <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-950 border-l-4 border-purple-400 rounded">
            <p className="flex items-center gap-2 text-sm text-purple-800 dark:text-purple-200">
              <Lightbulb className="w-4 h-4" />
              <span className="font-semibold">Tip:</span> {step.memoryTrick}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (isFormulaStep(step)) {
    return (
      <div className="border-l-4 border-indigo-500 pl-4 py-2">
        <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-gray-200">
          Step {number}: {step.title}
        </h4>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
          {step.description}
        </p>
        {step.formula && (
          <div className="my-3 p-4 bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 rounded-lg">
            {Array.isArray(step.formula) ? (
              step.formula.map((f, idx) => (
                <code
                  key={idx}
                  className="block font-mono text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-1"
                >
                  {f}
                </code>
              ))
            ) : (
              <code className="block font-mono text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                {step.formula}
              </code>
            )}
          </div>
        )}
        {step.visual && (
          <div className="my-3 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <pre className="font-mono text-sm text-gray-800 dark:text-gray-200 whitespace-pre">
              {step.visual.content}
            </pre>
          </div>
        )}
        {step.examples && step.examples.length > 0 && (
          <div className="ml-4 mt-3 space-y-1">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Examples:
            </p>
            {step.examples.map((example, idx) => (
              <ExampleRenderer key={idx} example={example} />
            ))}
          </div>
        )}
        {step.memoryTrick && (
          <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-950 border-l-4 border-purple-400 rounded">
            <p className="flex items-center gap-2 text-sm text-purple-800 dark:text-purple-200">
              <Lightbulb className="w-4 h-4" />
              <span className="font-semibold">Tip:</span> {step.memoryTrick}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (isConditionalStep(step)) {
    return (
      <div className="border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4 bg-amber-50 dark:bg-amber-950">
        <h4 className="font-semibold text-lg mb-3 text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <Info className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          Step {number}: {step.title}
        </h4>
        {step.content && (
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            {step.content}
          </p>
        )}
        <div className="space-y-3">
          {step.branches.map((branch, idx) => (
            <ConditionalBranchRenderer key={idx} branch={branch} />
          ))}
        </div>
        {step.examples && step.examples.length > 0 && (
          <div className="mt-4 ml-4 space-y-1">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Additional Examples:
            </p>
            {step.examples.map((example, idx) => (
              <ExampleRenderer key={idx} example={example} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default function StructuredExplanation({
  explanation,
}: StructuredExplanationProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>How It Works</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Overview */}
        {explanation.overview && (
          <p className="mb-6 text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
            {explanation.overview}
          </p>
        )}

        {/* Setup (for equations/systems) */}
        {explanation.setup && explanation.setup.system && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
              System:
            </p>
            {explanation.setup.system.map((eq, idx) => (
              <code
                key={idx}
                className="block font-mono text-blue-900 dark:text-blue-100"
              >
                {eq}
              </code>
            ))}
          </div>
        )}

        {/* Applicability */}
        {explanation.applicability && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2">
              When to use:
            </p>
            <p className="text-green-800 dark:text-green-200 mb-2">
              {explanation.applicability.condition}
            </p>
            {explanation.applicability.examples.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {explanation.applicability.examples.map((ex, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="bg-white dark:bg-gray-800"
                  >
                    {ex}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Steps */}
        <div className="space-y-6">
          {explanation.steps.map((step, idx) => (
            <StepRenderer key={idx} step={step} number={idx + 1} />
          ))}
        </div>

        {/* Special Cases */}
        {explanation.specialCases && explanation.specialCases.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Special Cases
            </h4>
            <div className="space-y-3">
              {explanation.specialCases.map((specialCase, idx) => (
                <div key={idx} className="border-l-2 border-gray-400 pl-3">
                  <p className="font-medium text-gray-700 dark:text-gray-300">
                    {specialCase.title}
                  </p>
                  {Object.entries(specialCase)
                    .filter(([key]) => key !== "title")
                    .map(([key, value]) => (
                      <p
                        key={key}
                        className="text-sm text-gray-600 dark:text-gray-400 mt-1"
                      >
                        <span className="font-semibold capitalize">
                          {key.replace(/_/g, " ")}:
                        </span>{" "}
                        {String(value)}
                      </p>
                    ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Memory Trick */}
        {explanation.memoryTrick && (
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-400 rounded">
            <p className="flex items-center gap-2 text-yellow-900 dark:text-yellow-100">
              <Lightbulb className="w-5 h-5" />
              <strong>Memory Trick:</strong> {explanation.memoryTrick}
            </p>
          </div>
        )}

        {/* Why It Works */}
        {explanation.whyItWorks && (
          <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg">
            <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">
              Why It Works
            </h4>
            <p className="text-purple-800 dark:text-purple-200">
              {explanation.whyItWorks}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
